import React, { useCallback, useState } from 'react';
import { View, Text, FlatList, TouchableOpacity, StyleSheet, Switch } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { theme } from '../../shared/theme/theme';
import { useAuth } from '../../infrastructure/auth/AuthContext';
import { JobsRepository } from '../../infrastructure/repositories/JobsRepository';
import { ProfileRepository } from '../../infrastructure/repositories/ProfileRepository';

interface JobRow {
  id: string;
  status: 'PENDING' | 'ACTIVE' | 'DONE';
  escrow_status: 'HELD' | 'RELEASED' | 'DISPUTED';
  created_at: string;
  services: { segment: string; type: string; price_per_hour: number } | null;
}

const getCurrentPosition = (): Promise<{ latitude: number; longitude: number } | null> =>
  new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000 },
    );
  });

export const HomeeJobsScreen = () => {
  const { session } = useAuth();
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [online, setOnline] = useState(false);

  const load = useCallback(async () => {
    if (!session) return;
    const data = await JobsRepository.listJobsForHomee(session.user.id);
    setJobs(data as unknown as JobRow[]);
  }, [session]);

  useFocusEffect(useCallback(() => { load(); }, [load]));

  const toggleOnline = async (value: boolean) => {
    if (!session) return;
    setOnline(value);
    await ProfileRepository.setActive(session.user.id, value);
    if (value) {
      const pos = await getCurrentPosition();
      if (pos) await ProfileRepository.updateLocation(session.user.id, pos.latitude, pos.longitude);
    }
  };

  const complete = async (jobId: string) => {
    await JobsRepository.completeJob(jobId);
    load();
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.title}>Moje zakázky</Text>
        <View style={styles.onlineRow}>
          <Text style={styles.onlineLabel}>{online ? 'Online' : 'Offline'}</Text>
          <Switch value={online} onValueChange={toggleOnline} trackColor={{ true: theme.colors.accent }} />
        </View>
      </View>

      <FlatList
        data={jobs}
        keyExtractor={(item) => item.id}
        contentContainerStyle={{ paddingBottom: 40 }}
        ListEmptyComponent={<Text style={styles.empty}>Zatím žádné přiřazené zakázky. Přepněte se Online pro zobrazení v okolí.</Text>}
        renderItem={({ item }) => (
          <View style={styles.card}>
            <Text style={styles.segment}>{item.services?.segment} · {item.services?.type}</Text>
            <Text style={styles.price}>{item.services?.price_per_hour} Kč / h</Text>
            {item.status === 'ACTIVE' && (
              <TouchableOpacity style={styles.completeButton} onPress={() => complete(item.id)}>
                <Text style={styles.completeText}>OZNAČIT JAKO DOKONČENO</Text>
              </TouchableOpacity>
            )}
            {item.status === 'DONE' && <Text style={styles.doneLabel}>Dokončeno · Escrow uvolněn</Text>}
          </View>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: theme.colors.background, padding: 24, paddingTop: 70 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 26, fontWeight: '900', color: theme.colors.text.main },
  onlineRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  onlineLabel: { color: theme.colors.text.muted, fontSize: 12, fontWeight: '700' },
  empty: { color: theme.colors.text.muted, textAlign: 'center', marginTop: 40 },
  card: { backgroundColor: theme.colors.surface, borderRadius: 12, padding: 16, marginBottom: 12, borderWidth: 1, borderColor: theme.colors.border },
  segment: { fontWeight: '900', color: theme.colors.text.main, fontSize: 14 },
  price: { color: theme.colors.accent, fontWeight: '700', marginTop: 4 },
  completeButton: { marginTop: 12, height: 44, backgroundColor: theme.colors.accent, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  completeText: { color: '#FFF', fontWeight: '900', fontSize: 12, letterSpacing: 1 },
  doneLabel: { marginTop: 10, color: '#2E7D32', fontWeight: '700', fontSize: 12 },
});
